/**
 * The operator-editable IP allowlist (§7.6), plus the one-shot enrolment code
 * bootstrap — both are answers to the same question: how does a console that
 * guards its own door let a brand-new network in?
 *
 * The environment's `ADMIN_IP_ALLOWLIST` is the immutable baseline (shown
 * read-only); this screen manages the DB-backed entries that extend it. The
 * middleware reads env + entries together per request, so a new IP works
 * immediately — which is the point of the form: add the network you are about
 * to use *while you are still on an allowed one*, because the allowlist also
 * guards login and a locked-out operator cannot reach this screen to fix it.
 *
 * Adding an IP needs to know the next address in advance, which a carrier NAT
 * does not give. The enrolment code is the version that works without that: a
 * superadmin mints one here, the blocked device types it into the login form,
 * and the first successful login redeems it. It is shown exactly once; the
 * codes table below only ever shows state, never the codes themselves.
 */

import { useEffect, useState } from 'react';
import { api } from '../api/client.ts';
import { at } from '../format.ts';
import { ConfirmButton, ErrorBanner, Field, Spinner } from '../components/ui.tsx';
import type { AdminIpAllowlistEntryView, EnrolmentCodeView } from '../api/types.ts';

export function IpAllowlist() {
  const [env, setEnv] = useState<string[] | null>(null);
  const [entries, setEntries] = useState<AdminIpAllowlistEntryView[] | null>(null);
  const [codes, setCodes] = useState<EnrolmentCodeView[] | null>(null);
  const [minted, setMinted] = useState<{ code: string; expiresAt: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [ip, setIp] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const load = async (): Promise<void> => {
    setError(null);
    try {
      const [allowlist, codesRes] = await Promise.all([api.ipAllowlist(), api.enrolmentCodes()]);
      setEnv(allowlist.env);
      setEntries(allowlist.entries);
      setCodes(codesRes.codes);
    } catch (err) {
      setError(err);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function add(): Promise<void> {
    if (!ip.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await api.addIpAllowlist(ip.trim(), note.trim() || undefined);
      setIp('');
      setNote('');
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function remove(target: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.removeIpAllowlist(target);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function mint(): Promise<void> {
    setBusy(true);
    setError(null);
    setMinted(null);
    try {
      const created = await api.mintEnrolmentCode();
      setMinted({ code: created.code, expiresAt: created.expiresAt });
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function revokeCode(id: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.revokeEnrolmentCode(id);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section>
      <header className="pagehead">
        <h1>IP allowlist</h1>
      </header>

      <ErrorBanner error={error} />
      {env === null || entries === null || codes === null ? <Spinner /> : null}

      {env && entries && codes ? (
        <>
          <div className="card">
            <h2>Baseline (environment)</h2>
            {env.length === 0 ? (
              <p className="muted">No environment allowlist — production refuses to start without one.</p>
            ) : (
              <div className="chips">
                {env.map((e) => (
                  <span key={e} className="chip">
                    {e}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="card">
            <h2>One-time enrolment code</h2>
            <p className="muted">
              For a brand-new phone whose network can't be allowlisted in advance — mint a code,
              enter it on the phone's login form, and the first successful login redeems it. It is
              shown here exactly once.
            </p>
            {minted ? (
              <div className="enrolment-mint">
                <button
                  type="button"
                  className="enrolment-code"
                  title="Copy"
                  onClick={() => {
                    void navigator.clipboard?.writeText(minted.code).then(() => {
                      setCopied(true);
                      window.setTimeout(() => setCopied(false), 1500);
                    });
                  }}
                >
                  <code>{minted.code}</code>
                  <span className="idchip__hint">{copied ? 'copied' : 'copy'}</span>
                </button>
                <p className="muted">Expires {at(minted.expiresAt)}. Treat it like a password.</p>
              </div>
            ) : (
              <ConfirmButton
                className="button button--primary"
                confirm="Mint a one-time enrolment code?"
                disabled={busy}
                onConfirm={() => void mint()}
              >
                Mint code
              </ConfirmButton>
            )}
          </div>

          <div className="card">
            <h2>Add an IP or network</h2>
            <p className="muted">
              Add it <em>before</em> switching networks — the allowlist guards login too.
            </p>
            <Field label="IP or CIDR">
              <input
                className="input"
                placeholder="e.g. 203.0.113.7 or 203.0.113.0/24"
                value={ip}
                onChange={(e) => setIp(e.target.value)}
              />
            </Field>
            <Field label="Note" hint="Who/where — e.g. 'home wifi'.">
              <input
                className="input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </Field>
            <ConfirmButton
              className="button button--primary"
              confirm={`Allow ${ip.trim() || 'this IP'} on the admin surface?`}
              disabled={busy || !ip.trim()}
              onConfirm={() => void add()}
            >
              Add
            </ConfirmButton>
          </div>

          <div className="card">
            <h2>Added entries</h2>
            {entries.length === 0 ? (
              <p className="muted">None yet.</p>
            ) : (
              <table className="table" role="table">
                <thead role="rowgroup">
                  <tr role="row">
                    <th scope="col" role="columnheader">IP / CIDR</th>
                    <th scope="col" role="columnheader">Note</th>
                    <th scope="col" role="columnheader">Added</th>
                    <th scope="col" role="columnheader" />
                  </tr>
                </thead>
                <tbody role="rowgroup">
                  {entries.map((e) => (
                    <tr role="row" key={e.ip}>
                      <td role="cell" data-label="IP / CIDR">
                        <code>{e.ip}</code>
                      </td>
                      <td role="cell" data-label="Note" className="muted">{e.note ?? '—'}</td>
                      <td role="cell" data-label="Added" className="muted">{at(e.createdAt)}</td>
                      <td role="cell" data-label="">
                        <ConfirmButton
                          className="button button--danger"
                          confirm={`Remove ${e.ip} from the allowlist?`}
                          disabled={busy}
                          onConfirm={() => void remove(e.ip)}
                        >
                          Remove
                        </ConfirmButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="card">
            <h2>Enrolment codes</h2>
            {codes.length === 0 ? (
              <p className="muted">None yet.</p>
            ) : (
              <table className="table" role="table">
                <thead role="rowgroup">
                  <tr role="row">
                    <th scope="col" role="columnheader">Minted by</th>
                    <th scope="col" role="columnheader">Minted</th>
                    <th scope="col" role="columnheader">Expires</th>
                    <th scope="col" role="columnheader">Status</th>
                    <th scope="col" role="columnheader" />
                  </tr>
                </thead>
                <tbody role="rowgroup">
                  {codes.map((c) => (
                    <tr role="row" key={c.id}>
                      <td role="cell" data-label="Minted by" className="muted">{c.mintedBy ?? '—'}</td>
                      <td role="cell" data-label="Minted" className="muted">{at(c.createdAt)}</td>
                      <td role="cell" data-label="Expires" className="muted">{at(c.expiresAt)}</td>
                      <td role="cell" data-label="Status">
                        <span className={`status status--${c.status}`}>{c.status}</span>
                      </td>
                      <td role="cell" data-label="">
                        {c.status === 'active' ? (
                          <ConfirmButton
                            className="button button--danger"
                            confirm="Revoke this code? It can no longer be used."
                            disabled={busy}
                            onConfirm={() => void revokeCode(c.id)}
                          >
                            Revoke
                          </ConfirmButton>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : null}
    </section>
  );
}